import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Warning } from "@phosphor-icons/react";
import { api, fmtErr } from "../../lib/api";
import DesignViewer from "../viewer3d/DesignViewer";
import SpecRail from "../viewer3d/SpecRail";

const BASE = process.env.REACT_APP_BACKEND_URL;

/** Studio 3D tab — the new spec-driven engine viewer (Phase 1 renders the authored
 *  reference Design Model) plus the CAD/STEP utilities the 2D print pipeline needs. */
export default function Model3dPanel({ projectId, generate, isGenerating }) {
  const [model, setModel] = useState(null);
  const [selected, setSelected] = useState(null);
  const [railOpen, setRailOpen] = useState(true);
  const viewerRef = useRef(null);

  const [generated, setGenerated] = useState(false);
  const [gen, setGen] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/engine/${projectId}/design-model`)
      .then(({ data }) => { setModel(data); setGenerated(true); })
      .catch(() => api.get("/engine/design-model/t401a")
        .then(({ data }) => { setModel(data); setGenerated(false); })
        .catch((e) => toast.error(fmtErr(e))));
  }, [projectId]);

  const generateModel = async () => {
    try {
      setGen(true);
      await api.post(`/engine/${projectId}/inputs/confirm`);
      const { data } = await api.post(`/engine/${projectId}/generate`);
      setModel(data);
      setGenerated(true);
      toast.success(`Design model generated — ${data.parts.length} parts from the spec`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setGen(false); }
  };

  const run = (jobType) => generate?.(jobType).catch((e) => toast.error(fmtErr(e)));

  return (
    <div className="absolute inset-0 flex flex-col bg-[var(--c-bg)]" data-testid="studio-3d-panel">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-1.5">
        <span className="font-mono text-[10px] text-[var(--c-muted)]">formigen.design/1.0 · Phase 1 viewer core</span>
        {generated ? (
          <span className="flex items-center gap-1.5 rounded-sm border border-[var(--c-green)]/40 bg-[var(--c-green)]/10 px-2 py-0.5 font-mono text-[10px] text-[var(--c-green)]"
            data-testid="studio-3d-generated-note">
            generated from this project&apos;s confirmed specification
          </span>
        ) : (
          <span className="flex items-center gap-1.5 rounded-sm border border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10 px-2 py-0.5 font-mono text-[10px] text-[var(--c-amber)]"
            data-testid="studio-3d-phase-note">
            <Warning size={11} /> reference model shown — generate from the spec to build this project
          </span>
        )}
        {model?.model_outdated && (
          <span className="flex items-center gap-1.5 rounded-sm border border-[var(--c-amber)]/40 bg-[var(--c-amber)]/10 px-2 py-0.5 font-mono text-[10px] text-[var(--c-amber)]"
            data-testid="studio-3d-outdated-note">
            <Warning size={11} /> built with older layout rules — regenerate to match the drawings
          </span>
        )}
        <button data-testid="studio-generate-model-btn" disabled={gen} onClick={generateModel}
          className="h-7 rounded-sm border border-[var(--c-accent)]/60 bg-[var(--c-accent)]/15 px-2.5 text-[11px] text-[var(--c-accent)] hover:bg-[var(--c-accent)]/25 disabled:opacity-40">
          {gen ? "Generating…" : generated ? "Regenerate from spec" : "Generate from spec"}
        </button>
        <div className="ml-auto flex items-center gap-1.5">
          <button data-testid="studio-cad-geometry-btn" disabled={isGenerating} onClick={() => run("generate_3d_model")}
            className="h-7 rounded-sm border border-[var(--c-border)] px-2.5 text-[11px] text-[var(--c-text)] hover:border-[var(--c-accent)] disabled:opacity-40">
            CAD geometry (STEP)
          </button>
          <button data-testid="studio-mfg-dxf-btn" disabled={isGenerating} onClick={() => run("print_drawing")}
            className="h-7 rounded-sm border border-[var(--c-accent)]/50 bg-[var(--c-accent)]/15 px-2.5 text-[11px] text-[var(--c-accent)] hover:bg-[var(--c-accent)]/25 disabled:opacity-40">
            MFG Drawing (DXF)
          </button>
          <a href={`${BASE}/api/drawings/${projectId}/3d-model.step`} target="_blank" rel="noreferrer"
            data-testid="studio-step-download"
            className="h-7 rounded-sm border border-[var(--c-border)] px-2.5 pt-1.5 font-mono text-[10px] text-[var(--c-muted)] hover:text-[var(--c-text)]">
            STEP
          </a>
          <button data-testid="studio-rail-toggle" onClick={() => setRailOpen((v) => !v)}
            className="h-7 rounded-sm border border-[var(--c-border)] px-2.5 text-[11px] text-[var(--c-muted)] hover:text-[var(--c-text)]">
            {railOpen ? "Hide spec" : "Show spec"}
          </button>
        </div>
      </div>

      <div className={`grid min-h-0 flex-1 gap-2 p-2 ${railOpen ? "lg:grid-cols-[minmax(0,1fr)_340px]" : "grid-cols-1"}`}>
        {model ? (
          <DesignViewer ref={viewerRef} model={model} onSelect={setSelected} className="h-full min-h-[22rem]" />
        ) : (
          <div className="grid place-items-center text-xs text-[var(--c-muted)]" data-testid="studio-3d-loading">Loading Design Model…</div>
        )}
        {railOpen && (
          <aside className="min-w-0 overflow-y-auto px-1">
            <SpecRail model={model} selected={selected}
              onSelect={(id) => { setSelected((c) => (c === id ? null : id)); viewerRef.current?.select(id); }} />
          </aside>
        )}
      </div>
    </div>
  );
}
